import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import { getCampaignList, deactivateCampaign } from 'app/api/campaign';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { APP_CONSTANTS, LocalSessionKey } from 'app/constants/app-constants';
import { LocalStorage } from 'app/utils/storage';

const useGamificationCampaigns = () => {
  const navigate = useNavigate();

  const [values, setValues] = useState({ status: '' });
  const [appliedFilters, setAppliedFilters] = useState({ status: '' });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isdeleteFiltering, setIsdeleteFiltering] = useState(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const currentPage = 1;
  const dataPerPage = 10;
  const [totalNumber, setTotalNumber] = useState(0);

  const sectionObj = {
    sectionTitle: 'Gamification Campaigns',
    searchObj: {
      keys: ['name'],
      placeholder: 'Search by Campaign name',
      searchType: 'server',
      handleSearch: () => {},
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen((prev) => !prev),
      },
      {
        imgTrue: true,
        customclassName: '',
        title: 'Create Campaign',
        handleBtnClick: () => {
          // Clear a lingering edit bridge (CAMP-07) before entering create
          // mode — otherwise a stale CAMPAIGN_DATA from a prior "Edit" click
          // would incorrectly hydrate this fresh Create form with the old
          // campaign's data.
          LocalStorage.remove(LocalSessionKey.CAMPAIGN_DATA);
          navigate(APP_CONSTANTS.GAMIFICATION_CREATE_CAMPAIGN);
        },
      },
    ],
  };

  const fetchCampaignData = useCallback(
    async (page, search = '', filters = appliedFilters) => {
      setPageLoading(true);
      try {
        const response = await getCampaignList({
          page,
          limit: dataPerPage,
          search,
          status: filters.status,
        });
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          setFilteredData(dataArr);
          const totalCount = response?.data?.count || 0;
          setTotalNumber(totalCount);
          setPageCount(Math.ceil(totalCount / dataPerPage));
          setIsdeleteFiltering(false);
        } else {
          Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage, appliedFilters]
  );

  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [isFiltering]);

  function handleSubmit(event) {
    event.preventDefault();
    setAppliedFilters(values);
    setIsFilterOpen(false);
    setIsFiltering(true);
  }

  function handleFilterCancel() {
    setIsFilterOpen(false);
  }

  function resetfilter() {
    const empty = { status: '' };
    setValues(empty);
    setAppliedFilters(empty);
    setIsFiltering(true);
  }

  // CAMP-07: repurposed from a read-only "View" navigation (detail route)
  // into the real editable prefill flow — the row action's icon/tooltip
  // meaning shifts View -> Edit (03-UI-SPEC.md), no second action is added.
  // The LocalStorage bridge write is unchanged (RESEARCH Pattern 5); only
  // the navigation target changes to the shared create/edit form.
  function handleView(data) {
    LocalStorage.set(LocalSessionKey.CAMPAIGN_DATA, JSON.stringify(data));
    navigate(APP_CONSTANTS.GAMIFICATION_CREATE_CAMPAIGN);
  }

  async function callDeactivate(campaign) {
    setPageLoading(true);
    const responseObj = await deactivateCampaign(campaign?.id);
    setPageLoading(false);
    if (responseObj?.responseCode === 200) {
      setIsdeleteFiltering(true);
      Toast.success('Campaign deactivated successfully!');
    } else {
      Toast.error(responseObj?.response?.message || Message.Error.COMMON_MESSAGE);
    }
  }

  function handleDeactivate(campaign) {
    confirmAlert({
      title: '',
      message:
        'Are you sure you want to deactivate this campaign? It will stop being available to customers, but its history stays visible in reporting.',
      buttons: [
        { label: 'Yes', onClick: () => callDeactivate(campaign) },
        { label: 'No' },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  }

  return {
    filteredData,
    appliedFilters,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    isFiltering,
    isdeleteFiltering,
    pageLoading,
    currentPage,
    dataPerPage,
    pageCount,
    totalNumber,
    fetchCampaignData,
    handleSubmit,
    handleFilterCancel,
    resetfilter,
    handleView,
    handleDeactivate,
    callDeactivate,
    sectionObj,
  };
};

export default useGamificationCampaigns;
